import styled from 'styled-components'
import { Autoplay, EffectFade, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { type DemoSliderData } from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { spacing } from '@/features/shared/theme/tokens'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Body, Display, Headline } from '@shipt/design-system-typography'
import { screenSizes } from '@shipt/design-system-themes'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `demo_slider_container` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/demo_slider_container
 * - **Production** - https://cms.shipt.com/content-type/pool/demo_slider_container
 */
export const DemoSlider = ({
  content_type_id,
  id,
  data: { heading, subheading, references },
}: DemoSliderData) => {
  const { screenSm: isMobile } = useMediaQuery()
  const imageHeight = isMobile ? 420 : 555
  const imageWidth = isMobile ? 200 : 264

  try {
    return (
      <Container {...getCMSWrapperProps({ content_type_id, id })}>
        <Heading as="h2" surface="inverse" size="sm">
          {heading}
        </Heading>
        <SubHeading size="xl" surface="inverse">
          {subheading}
        </SubHeading>
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          slidesPerView={1}
          loop
          className="swiper-container-fade"
          effect="fade"
          fadeEffect={{ crossFade: true }}
          pagination={{ clickable: true, el: '.swiper-pagination' }}
          autoplay={{ delay: 7500, disableOnInteraction: false }}
        >
          {references.map(({ data: { heading: imageHeading, caption, image } }) => (
            <SwiperSlide key={caption} className="swiper-slide">
              <PanelImage src={image.src} alt={image.alt} height={imageHeight} width={imageWidth} />
              <PanelText>
                <PanelHeading as="h3" size="xl">
                  {imageHeading}
                </PanelHeading>
                <PanelCaption>{caption}</PanelCaption>
              </PanelText>
            </SwiperSlide>
          ))}
          <PaginationContainer className="swiper-pagination" />
        </Swiper>
      </Container>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const Container = styled.section`
  position: relative;
  text-align: center;
  width: 100%;
  isolation: isolate;

  .swiper-container-fade .swiper-slide {
    transition-property: opacity;
  }

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    background-color: ${theme.plum};
    height: 60%;
    width: 100%;
  }
`

const Heading = styled(Display)`
  margin: ${spacing(75, 'auto', 'lg')};
  max-width: 16.3125rem;

  @media ${screenSizes.tablet} {
    max-width: 27.5rem;
  }
`

const SubHeading = styled(Body)`
  margin: ${spacing(0, 'auto', 'xxl')};
  max-width: 21.4375rem;

  @media ${screenSizes.tablet} {
    max-width: 27.5rem;
  }
`

const PanelImage = styled(Image)`
  max-width: 12.5rem;
  max-height: 26.25rem;

  @media ${screenSizes.tablet} {
    max-width: 16.5rem;
    max-height: 34.6875rem;
  }
`

const PanelText = styled.div`
  max-width: 27.25rem;
  margin: ${spacing(0, 'auto')};
`

const PanelHeading = styled(Headline)`
  margin: ${spacing(0, 'auto', 'sm')};
  max-width: 21.4375rem;
`

const PanelCaption = styled(Body)`
  margin: ${spacing(0, 'auto')};
  max-width: 21.4375rem;

  @media ${screenSizes.tablet} {
    max-width: 27.25rem;
  }
`

const PaginationContainer = styled.div`
  margin: ${spacing('lg', 'auto')};
  z-index: 2;
  position: relative;

  @media ${screenSizes.tablet} {
    margin: ${spacing(30, 'auto', 'lg')};
  }

  .swiper-pagination-bullet {
    width: 0.5rem;
    height: 0.5rem;
    display: inline-block;
    border-radius: 100%;
    background: ${theme.black};
    opacity: 0.2;
    margin-right: ${spacing(11)};
  }

  .swiper-pagination-bullet-active {
    color: ${theme.gray800};
    opacity: 1;
  }
`
